import { EnumButtonName } from '@calm/evexia-analytics';
import ValidatedTextField, {
    ValidatedTextFieldProps,
} from '@rhg/components/validated-text-field';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    Button,
    DialogActions,
    Typography,
    type TextField,
    type TextFieldProps,
} from '@rhg/libs/material';
import {
    LocalizationProvider,
    DatePickerProps,
    DatePicker,
    PickersActionBarProps,
} from '@rhg/libs/material/date-pickers';
import { displayLocaleCustomString } from '@rhg/utils/date-time';
import { useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

const stylePopper = {
    width: { xs: '100%', sm: '100%', md: 'auto' },
};
const stylePaper = {
    backgroundColor: 'background.paper',
};

interface Props {
    name: string;
    control: Control<any>;
    label: string;
    format?: string;
    required?: boolean;
    textFieldProps?: Partial<ValidatedTextFieldProps>;
    datePickerProps?: Partial<DatePickerProps<Date>>;
}

const styleActionBarContainer = { width: '100%', mb: 4, mx: 2 };
const styleActionBarPreviewTextContainer = {
    my: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};
const styleActionBarButton = { textTransform: 'uppercase' };

const CustomActionBar = ({
    value,
    label,
    className,
    onAccept,
}: PickersActionBarProps & {
    value?: Date | null;
    label?: string;
    onAccept?: () => void;
}) => {
    const { formatMessage } = useIntl();

    const formattedDate = useMemo(() => {
        if (!value) {
            return '';
        }

        try {
            return displayLocaleCustomString(
                {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                },
                new Date(value)
            );
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            // Handle invalid time value and other date errors
            return '';
        }
    }, [value]);

    const formattedLabel = useMemo(
        () => (label ? label.replace('(Required)', '').trim() : ''),
        [label]
    );
    if (!formattedDate) {
        return null;
    }
    return (
        <DialogActions className={className}>
            <Box sx={styleActionBarContainer}>
                <Box sx={styleActionBarPreviewTextContainer}>
                    <Typography variant="subtitle2" color="text.sub">
                        {formattedLabel}
                    </Typography>
                    <Typography variant="body1Demi">{formattedDate}</Typography>
                </Box>
                <Button
                    onClick={onAccept}
                    trackButton={EnumButtonName.Confirm}
                    fullWidth
                    variant="contained"
                    size="small"
                    sx={styleActionBarButton}
                >
                    {formatMessage(commonMessages['common.ok'])}
                </Button>
            </Box>
        </DialogActions>
    );
};

// `field.value` reaches the picker as a `Date` (set by the picker itself, or by
// a Date-typed form like age-and-gender) or as a string — eligibility's dynamic
// form seeds the field with `''`, and a pre-filled/serialized default would
// arrive as a date string. Coerce to the `Date | null` the picker consumes:
// parse a non-empty string (so an incoming value is shown, not dropped), and
// treat `''`/null/undefined as "no date selected". Real narrowing, no cast.
const toPickerDate = (value: unknown): Date | null => {
    if (value instanceof Date) {
        return value;
    }
    if (typeof value === 'string' && value !== '') {
        return new Date(value);
    }
    return null;
};

const FormDatePicker = ({
    name,
    control,
    label,
    textFieldProps,
    datePickerProps,
    format,
    required = false,
}: Props) => {
    return (
        <LocalizationProvider>
            <Controller
                name={name}
                control={control}
                render={(props) => {
                    const rawValue: unknown = props.field.value;
                    const fieldValue = toPickerDate(rawValue);
                    return (
                        <DatePicker
                            {...datePickerProps}
                            // The toolbar below is hidden, so MUI names the
                            // calendar dialog from this `label` prop instead
                            // (its documented fallback). The visible text
                            // field's label still comes from
                            // slotProps.textField.label below, which wins
                            // for rendering — this only feeds the dialog's
                            // accessible name.
                            label={label}
                            format={format}
                            value={fieldValue}
                            onChange={props.field.onChange}
                            closeOnSelect={false}
                            disableHighlightToday
                            slotProps={{
                                popper: { sx: stylePopper },
                                desktopPaper: { sx: stylePaper },
                                mobilePaper: { sx: stylePaper },
                                // The picker's internal IconButtons render
                                // with no `size`, which now resolves to the
                                // Figma 48px medium box; pin them to
                                // legacyMedium to keep the pre-size-scale
                                // 40px geometry.
                                openPickerButton: { size: 'legacyMedium' },
                                previousIconButton: { size: 'legacyMedium' },
                                nextIconButton: { size: 'legacyMedium' },
                                switchViewButton: { size: 'legacyMedium' },
                                textField: {
                                    name,
                                    required,
                                    label,
                                    error: !!props.fieldState.error,
                                    errorText: props.fieldState.error?.message,
                                    ...textFieldProps,
                                    // Must win over textFieldProps: this is the
                                    // react-hook-form Controller ref that
                                    // setFocus() needs registered on the real
                                    // input DOM node. Placed last so a caller's
                                    // own textFieldProps.inputRef can never
                                    // silently drop it.
                                    inputRef: props.field.ref,
                                } as TextFieldProps,
                                actionBar: {
                                    actions: ['accept'],
                                    value: fieldValue,
                                    label,
                                } as unknown as PickersActionBarProps,
                                toolbar: { hidden: true },
                            }}
                            slots={{
                                textField:
                                    ValidatedTextField as typeof TextField,
                                actionBar: CustomActionBar,
                            }}
                        />
                    );
                }}
            />
        </LocalizationProvider>
    );
};

export default FormDatePicker;
